import { Temporal } from "temporal-polyfill";
import * as Sentry from "@sentry/browser";
import { toast } from "sonner";
import { router } from "@/router";
import { isNative } from "./environment";
import { readSessionMarker, writeSessionMarker } from "./session-marker";

/** Written by `buildVersionPlugin` in `vite.config.ts`. */
const VERSION_MANIFEST_PATH = "/version.json";

/** Records which deployed version we already reloaded for, to detect a loop. */
const RELOAD_MARKER_KEY = "saile.deployedVersionReloadedFor";

/**
 * A tab left open all day should not poll often; the visibility check is what
 * makes a returning user notice a deploy promptly, so this is only a backstop
 * for a tab that stays visible for hours.
 */
const POLL_INTERVAL_MS = Temporal.Duration.from({ minutes: 15 }).total(
  "milliseconds",
);

/** The prompt stays until the user acts on it or navigates. */
const TOAST_DURATION_MS = Number.POSITIVE_INFINITY;

/**
 * Set once a newer deploy has been seen, which both suppresses repeat prompts
 * and arms the reload on the next navigation.
 */
let pendingVersion: string | null = null;

const readDeployedVersion = async (): Promise<string | null> => {
  try {
    // `no-store` keeps the browser's HTTP cache — the thing that hides a deploy
    // in the first place — out of the check itself.
    const response = await fetch(VERSION_MANIFEST_PATH, { cache: "no-store" });
    if (!response.ok) {
      return null;
    }
    const manifest: unknown = await response.json();
    const version = (manifest as { version?: unknown }).version;
    if (typeof version !== "string" || !version) {
      return null;
    }
    return version;
  } catch {
    // Offline or a transient failure. The next trigger tries again.
    return null;
  }
};

/**
 * Replaces the document rather than reusing the running app, so the browser
 * fetches the current `index.html` and the chunk names it names.
 */
const reloadAtHref = (href: string) => {
  if (pendingVersion) {
    writeSessionMarker(RELOAD_MARKER_KEY, pendingVersion);
  }
  window.location.assign(href);
};

/**
 * Having already reloaded for this exact version means the new build is still
 * not reaching this client — an edge or proxy cache is holding the old
 * `index.html`. Reloading again would loop, so report it and leave the tab
 * alone; the user keeps a working, if superseded, app.
 */
const hasAlreadyReloadedFor = (version: string): boolean => {
  if (readSessionMarker(RELOAD_MARKER_KEY) !== version) {
    return false;
  }
  Sentry.captureMessage("Client still stale after version reload", {
    level: "warning",
    tags: {
      deployed_version: version,
      running_version: __BUILD_VERSION__,
    },
  });
  return true;
};

const announceNewVersion = (version: string) => {
  if (hasAlreadyReloadedFor(version)) {
    return;
  }

  pendingVersion = version;
  toast.info("A new version of Saile is available.", {
    description: "Reload to get the latest updates.",
    duration: TOAST_DURATION_MS,
    action: {
      label: "Reload",
      onClick: () => reloadAtHref(window.location.href),
    },
  });
};

const checkDeployedVersion = async () => {
  // Already announced; nothing a further check could add.
  if (pendingVersion) {
    return;
  }

  const deployedVersion = await readDeployedVersion();
  if (!deployedVersion || deployedVersion === __BUILD_VERSION__) {
    return;
  }
  announceNewVersion(deployedVersion);
};

/**
 * Notices when the running build has been superseded and gets the client onto
 * the current one.
 *
 * `installStaleBundleReload` only recovers clients that fail to load a chunk,
 * which requires the stale tab to reach a lazily-loaded route. Most routes are
 * in the main bundle, so a tab that loaded before a deploy otherwise keeps
 * rendering old code indefinitely — a single-page app never re-requests
 * `index.html` on its own.
 */
export const installDeployedVersionCheck = () => {
  // Native bundles are replaced by Capacitor LiveUpdate in `liveReload.ts`. A
  // web reload there would only reload the bundle already on the device.
  if (isNative()) {
    return;
  }

  // A dev server or local build has no deploy identity to compare against.
  if (!__BUILD_VERSION__) {
    return;
  }

  // The load that just happened may itself have come from cache, so check now
  // rather than waiting for the first interval.
  void checkDeployedVersion();

  window.setInterval(() => void checkDeployedVersion(), POLL_INTERVAL_MS);

  // Navigating is when a full page load costs the user least: the destination is
  // about to render from scratch anyway, and nothing on the page they are
  // leaving is mid-edit. Loading the destination directly keeps the navigation
  // they asked for.
  router.subscribe("onBeforeNavigate", ({ toLocation }) => {
    if (!pendingVersion) {
      return;
    }
    reloadAtHref(toLocation.href);
  });
};
